import classNames from "classnames";
import { useState, useMemo, useEffect } from "react";
import { GuildData } from "../../../../../../common/sesh_types";
import { DashboardUtils } from "../../../dashboard_utils";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";

export type ActivationType = "premium" | "pro";

export interface ServerActivationModalProps {
	open: boolean;
	onClose: () => void;
	unassignedCount: number;
	guilds: GuildData[];
	onAssign: (guildIds: string[]) => void;
	type: ActivationType;
}

type SortedGuild = GuildData & {
	sortPriority: number; // Lower is higher priority
	statusLabel: string | null;
	isDisabled: boolean;
};

// Config for different activation types
const typeConfig = {
	premium: {
		title: "Activate Premium",
		icon: "fas fa-gem",
		iconColor: "text-sesh-primary",
		bannerBorder: "border-sesh-primary/30",
		bannerBg: "bg-sesh-primary/10",
		bannerTextColor: "text-cyan-200",
		label: "Premium",
		buttonText: "Activate Premium",
	},
	pro: {
		title: "Activate sesh Pro",
		icon: "fas fa-crown",
		iconColor: "text-amber-300",
		bannerBorder: "border-amber-500/30",
		bannerBg: "bg-amber-500/10",
		bannerTextColor: "text-amber-200",
		label: "sesh Pro",
		buttonText: "Activate sesh Pro",
	},
};

export function ServerActivationModal(props: ServerActivationModalProps) {
	const { open, onClose, unassignedCount, guilds, onAssign, type } = props;
	const [selectedGuildIds, setSelectedGuildIds] = useState<Set<string>>(
		new Set()
	);
	const [isAssigning, setIsAssigning] = useState(false);

	const config = typeConfig[type];

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setSelectedGuildIds(new Set());
			setIsAssigning(false);
		}
	}, [open]);

	// Sort guilds based on activation type
	const sortedGuilds = useMemo<SortedGuild[]>(() => {
		return guilds
			.filter((g) => g.has_sesh)
			.map((guild) => {
				let sortPriority: number;
				let statusLabel: string | null = null;
				let isDisabled = false;

				if (type === "pro") {
					// For Pro assignment
					if (guild.has_sesh_pro) {
						sortPriority = 2;
						statusLabel = "sesh Pro";
						isDisabled = true;
					} else if (guild.is_premium || guild.is_discord_premium) {
						sortPriority = 0;
						statusLabel = "Premium";
					} else {
						sortPriority = 1;
					}
				} else {
					// For Premium assignment
					if (guild.is_premium || guild.is_discord_premium) {
						sortPriority = 2;
						statusLabel = guild.has_sesh_pro ? "sesh Pro" : "Premium";
						isDisabled = true;
					} else {
						sortPriority = 0;
					}
				}

				return {
					...guild,
					sortPriority,
					statusLabel,
					isDisabled,
				};
			})
			.sort((a, b) => {
				if (a.sortPriority !== b.sortPriority) {
					return a.sortPriority - b.sortPriority;
				}
				return a.name.localeCompare(b.name);
			});
	}, [guilds, type]);

	if (!open) {
		return null;
	}

	const handleToggleGuild = (guildId: string) => {
		setSelectedGuildIds((prev) => {
			const next = new Set(prev);
			if (next.has(guildId)) {
				next.delete(guildId);
			} else if (next.size < unassignedCount) {
				next.add(guildId);
			}
			return next;
		});
	};

	const handleAssign = () => {
		console.log("handleAssign - selectedGuildIds:", Array.from(selectedGuildIds));
		console.log("handleAssign - unassignedCount:", unassignedCount);
		setIsAssigning(true);
		onAssign(Array.from(selectedGuildIds));
	};

	const remainingSlots = unassignedCount - selectedGuildIds.size;

	return (
		<DashboardUtils.SeshModal
			is_active={open}
			onClose={onClose}
			title={config.title}
			subtitle={`Select ${unassignedCount === 1 ? "a server" : `up to ${unassignedCount} servers`} to activate ${config.label}`}
			content={
				<div className="mt-4 flex flex-col gap-4">
					{/* Info banner */}
					<div
						className={classNames(
							"rounded-lg border p-3",
							config.bannerBorder,
							config.bannerBg
						)}
					>
						<div
							className={classNames(
								"flex items-center gap-2 text-sm",
								config.bannerTextColor
							)}
						>
							<i className={classNames(config.icon, config.iconColor)}></i>
							<span>
								You have{" "}
								<span className="font-semibold">
									{unassignedCount}
								</span>{" "}
								{config.label} activation
								{unassignedCount > 1 ? "s" : ""} to assign
							</span>
						</div>
					</div>

					{/* Server selection counter */}
					<div className="flex items-center justify-between text-sm">
						<span className="text-slate-300">
							Selected: {selectedGuildIds.size} /{" "}
							{unassignedCount}
						</span>
						{remainingSlots > 0 && selectedGuildIds.size > 0 && (
							<span className="text-slate-400">
								{remainingSlots} slot
								{remainingSlots > 1 ? "s" : ""} remaining
							</span>
						)}
					</div>

					{/* Server list */}
					<div className="max-h-[300px] overflow-y-auto rounded-lg border border-slate-600 bg-slate-900/50">
						{sortedGuilds.length === 0 ? (
							<div className="p-4 text-center text-slate-400">
								<i className="fas fa-server mb-2 text-2xl"></i>
								<p>
									No servers with sesh found. Please invite
									sesh to a server first.
								</p>
							</div>
						) : (
							<ul className="divide-y divide-slate-700">
								{sortedGuilds.map((guild) => {
									const isSelected = selectedGuildIds.has(
										guild.id
									);
									const canSelect =
										!guild.isDisabled &&
										(isSelected ||
											selectedGuildIds.size <
												unassignedCount);

									const icon = DashboardUtils.GetGuildIconURI(
										{ id: guild.id, icon: guild.icon },
										64
									);

									return (
										<li
											key={guild.id}
											onClick={() => {
												if (canSelect) {
													handleToggleGuild(guild.id);
												}
											}}
											className={classNames(
												"flex cursor-pointer items-center gap-3 p-3 transition-colors",
												{
													"bg-sesh-primary/10":
														isSelected,
													"hover:bg-slate-800":
														canSelect && !isSelected,
													"cursor-not-allowed opacity-50":
														!canSelect,
												}
											)}
										>
											{/* Checkbox */}
											<div
												className={classNames(
													"flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border",
													{
														"border-sesh-primary bg-sesh-primary":
															isSelected,
														"border-slate-500":
															!isSelected &&
															canSelect,
														"border-slate-600":
															!canSelect,
													}
												)}
											>
												<i
												className={classNames(
													"fas fa-check text-xs text-base-dark",
													{ invisible: !isSelected }
												)}
											></i>
											</div>

											{/* Server icon */}
											<img
												src={icon}
												alt=""
												className="h-10 w-10 rounded-full"
											/>

											{/* Server info */}
											<div className="flex min-w-0 flex-1 flex-col">
												<span
													className={classNames(
														"truncate font-medium",
														{
															"text-white":
																canSelect,
															"text-slate-400":
																!canSelect,
														}
													)}
												>
													{guild.name}
												</span>
												{guild.statusLabel && (
													<span
														className={classNames(
															"text-xs",
															{
																"text-amber-300":
																	guild.statusLabel ===
																	"sesh Pro",
																"text-sesh-primary":
																	guild.statusLabel ===
																	"Premium",
															}
														)}
													>
														{guild.statusLabel}
														{guild.isDisabled &&
															" (already active)"}
													</span>
												)}
											</div>
										</li>
									);
								})}
							</ul>
						)}
					</div>

					{/* Action buttons */}
					<div className="flex flex-col gap-2">
						<SeshPrimaryButton
							onClick={handleAssign}
							disabled={
								selectedGuildIds.size === 0 || isAssigning
							}
							is_loading={isAssigning}
							className={classNames(
								"w-full justify-center font-semibold",
								{
									"bg-sesh-primary !text-base-dark hover:bg-cyan-300":
										selectedGuildIds.size > 0 &&
										!isAssigning,
									"opacity-50": selectedGuildIds.size === 0,
								}
							)}
						>
							<i
								className={classNames(
									config.icon,
									config.iconColor,
									"mr-2"
								)}
							></i>
							{config.buttonText}
							{selectedGuildIds.size > 0 &&
								` on ${selectedGuildIds.size} Server${selectedGuildIds.size > 1 ? "s" : ""}`}
						</SeshPrimaryButton>

						<button
							onClick={onClose}
							className="text-sm text-slate-400 hover:text-white"
						>
							Skip for now (you can assign later)
						</button>
					</div>
				</div>
			}
		/>
	);
}

// Keep the old export for backwards compatibility
export const ProAssignmentModal = (
	props: Omit<ServerActivationModalProps, "type">
) => <ServerActivationModal {...props} type="pro" />;
